"use client";

import { type ElementType, useState } from "react";
import { ChevronDown } from "lucide-react";
import type { BundledLanguage } from "shiki";
import { InlineSvg } from "@/components/InlineSvg";
import { Skeleton } from "@/components/shadcn/skeleton";
import { CodeBlock } from "@/components/ai-elements/code-block";
import type { NoxusFormEntryConfig } from "@/components/Spotflow/ConfigDrawer/ConfigDrawer";
import type { ConfigBindableCode } from "@/types/api/spotflow/spotflow-config";
import { DATA_KEYS, useRemoteData } from "@/utils/useRemoteData";
import { getNodeCategory } from "@/components/SpotflowV2/nodeCategories";
import { useSpotUser } from "@/utils/spotUser";
import { cn } from "@/lib/utils";
import { EntityPill, OpBadge } from "./EntityApprovalBody";
import {
  type ApprovalNode,
  findNodeByHandle,
  useApprovalFlowNodes,
} from "./flowNodes";
import { humanize, isEmptyValue } from "./approvalValues";
import {
  handleLabel,
  type NodeNameOf,
  type RowChange,
  rowChanges,
  rowChangesSummary,
  valueLines,
} from "./flowEditValues";
import type {
  EditedField,
  WorkflowApprovalAction,
  WorkflowApprovalPlan,
} from "./workflowApprovalActions";

type NodeTypeDisplay = {
  title: string;
  image: string | null;
  Icon: ElementType;
  iconColor: string;
  iconBg: string;
};

// Until the display list lands there is nothing to show but a placeholder:
// the raw type and the category fallback icon read as a broken card.
function useNodeTypeDisplay(nodeType: string): NodeTypeDisplay | null {
  const { data } = useRemoteData(DATA_KEYS.NODE_DISPLAY);
  if (!data) return null;
  const def = data.find((n) => n.type === nodeType);
  const cat = getNodeCategory({
    type: nodeType,
    category: def?.category ?? null,
  });
  return {
    title: def?.title || nodeType,
    image: def?.image ?? null,
    Icon: cat.icon,
    iconColor: cat.iconColor,
    iconBg: cat.iconBg,
  };
}

function NodeTypeIcon({ display }: { display: NodeTypeDisplay | null }) {
  if (!display)
    return (
      <Skeleton className="h-5 w-5 shrink-0 rounded bg-muted-foreground/15" />
    );
  const { image, Icon, iconColor, iconBg } = display;
  return (
    <span
      className={cn(
        "flex h-5 w-5 items-center justify-center shrink-0 rounded",
        iconBg,
        iconColor
      )}
    >
      {image ? (
        <InlineSvg src={image} className="size-3.5" recolorStroke={false} />
      ) : (
        <Icon className="size-3.5" />
      )}
    </span>
  );
}

// A new step, which opens to the settings it is created with and the steps
// it runs after, read as an edit step reads its changes.
function AddRow({
  action,
  nameOf,
}: {
  action: Extract<WorkflowApprovalAction, { op: "add" }>;
  nameOf: NodeNameOf;
}) {
  const [open, setOpen] = useState(false);
  const display = useNodeTypeDisplay(action.nodeType);
  const schemas = useNodeFieldSchemas(action.nodeType);
  const title = display?.title;
  const fields = Object.entries(action.config).filter(
    ([, value]) => !isEmptyValue(value)
  );
  const hasDetail = fields.length > 0 || action.after.length > 0;
  const head = (
    <>
      <NodeTypeIcon display={display} />
      {title ? (
        <span className="font-medium text-foreground">{title}</span>
      ) : (
        <Skeleton className="h-3.5 w-20 bg-muted-foreground/15" />
      )}
      {action.name && action.name !== title && (
        <span className="min-w-0 truncate text-muted-foreground">
          "{action.name}"
        </span>
      )}
    </>
  );
  return (
    <div className="flex items-start gap-2 text-xs pt-[6px]">
      <OpBadge op="add" className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {hasDetail ? (
            <button
              type="button"
              aria-label={`${open ? "Hide" : "Show"} ${action.name || title || "step"}'s settings`}
              aria-expanded={open}
              onClick={() => setOpen(!open)}
              className="flex min-w-0 items-center gap-2 cursor-pointer text-left"
            >
              {head}
              <ChevronDown
                className={cn(
                  "size-3 shrink-0 text-muted-foreground transition-transform",
                  open && "rotate-180"
                )}
              />
            </button>
          ) : (
            head
          )}
          {action.table && (
            <EntityPill entity={{ kind: "table", id: action.table }} />
          )}
        </div>
        {open && (
          <div className="mt-1.5 space-y-1">
            {fields.map(([name, value]) => (
              <ScalarFieldRow
                key={name}
                field={{ name, newValue: value }}
                oldRaw={undefined}
                nameOf={nameOf}
                language={codeLanguageOf(schemas[name], action.config)}
              />
            ))}
            {action.after.length > 0 && (
              <div className="flex items-baseline gap-2">
                <span className={LABEL}>Runs after</span>
                <span className="min-w-0 text-foreground">
                  {action.after
                    .map((handle) => handleLabel(handle, nameOf))
                    .join(", ")}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const SUMMARY_LENGTH = 48;

function truncate(text: string): string {
  return text.length > SUMMARY_LENGTH
    ? text.slice(0, SUMMARY_LENGTH) + "…"
    : text;
}

// A node added in this call is referred to by its key until it exists.
function nodeNameOf(
  nodes: ApprovalNode[],
  actions: WorkflowApprovalAction[]
): NodeNameOf {
  const added = new Map(
    actions.flatMap((a) =>
      a.op === "add" && a.key ? [[a.key, a.name] as const] : []
    )
  );
  return (handle) =>
    added.get(handle) ?? findNodeByHandle(nodes, handle)?.name ?? undefined;
}

function RemoveRow({
  action,
  node,
}: {
  action: Extract<WorkflowApprovalAction, { op: "remove" }>;
  node: ApprovalNode | undefined;
}) {
  const display = useNodeTypeDisplay(node?.nodeType ?? "");
  const label = node?.name || action.handle;
  return (
    <div className="flex items-center gap-2 text-xs pt-[6px]">
      <OpBadge op="remove" />
      <NodeTypeIcon display={display} />
      <span className="font-medium text-foreground line-through decoration-red-400/70">
        {label}
      </span>
    </div>
  );
}

function RenameRow({
  action,
  node,
}: {
  action: Extract<WorkflowApprovalAction, { op: "rename" }>;
  node: ApprovalNode | undefined;
}) {
  const display = useNodeTypeDisplay(node?.nodeType ?? "");
  return (
    <div className="flex items-center gap-2 text-xs pt-[6px]">
      <OpBadge op="edit" />
      <NodeTypeIcon display={display} />
      <Renamed from={node?.name || action.handle} to={action.newName} />
    </div>
  );
}

function Renamed({ from, to }: { from: string; to: string }) {
  return (
    <>
      <span className="min-w-0 truncate text-red-400/80 line-through">
        {from}
      </span>
      <span className="shrink-0 text-muted-foreground">→</span>
      <span className="min-w-0 truncate font-medium text-foreground">{to}</span>
    </>
  );
}

function RowChangeLines({ changes }: { changes: RowChange[] }) {
  return (
    <div className="mt-1 ml-[3.5rem] space-y-0.5 font-mono text-[11px] leading-relaxed">
      {changes.map((change, i) => (
        <div key={i} className="whitespace-pre-wrap break-words">
          {change.kind !== "added" && (
            <span className="text-red-400/80 line-through">
              {change.before}
            </span>
          )}
          {(change.kind === "changed" || change.kind === "reordered") && (
            <span className="text-muted-foreground"> → </span>
          )}
          {change.kind !== "removed" && (
            <span className="text-emerald-600">{change.after}</span>
          )}
        </div>
      ))}
    </div>
  );
}

type FieldSchemas = Record<string, NoxusFormEntryConfig>;

// The node type's config fields as the editor sidebar draws them.
function useNodeFieldSchemas(nodeType: string): FieldSchemas {
  const { data } = useRemoteData(DATA_KEYS.NODES);
  return (data?.find((n) => n.type === nodeType)?.config ?? {}) as FieldSchemas;
}

// A field the editor edits as code, in the language it highlights it in:
// Run Code's editor is Python, a bindable code field names its language or
// the sibling field that picks it, as BindableCodeField reads them.
function codeLanguageOf(
  schema: NoxusFormEntryConfig | undefined,
  values: Record<string, unknown>
): BundledLanguage | null {
  const display = schema?.display;
  if (display?.type === "text_code_ai") return "python";
  if (display?.type !== "config_bindable_code") return null;
  const code = display as ConfigBindableCode;
  if (code.markdown_mode_field && values[code.markdown_mode_field] === true)
    return null;
  const sibling = code.language_field ? values[code.language_field] : null;
  const language =
    sibling === "json" || sibling === "xml" || sibling === "text"
      ? sibling
      : code.language;
  return language === "json" || language === "xml" || language === "sql"
    ? language
    : null;
}

const LABEL = "shrink-0 text-muted-foreground capitalize min-w-[3rem]";

function ScalarFieldRow({
  field,
  oldRaw,
  nameOf,
  language,
}: {
  field: EditedField;
  oldRaw: unknown;
  nameOf: NodeNameOf;
  language: BundledLanguage | null;
}) {
  const [open, setOpen] = useState(false);
  const hasOld = !isEmptyValue(oldRaw);
  const isVar =
    typeof field.newValue === "string" && field.newValue.startsWith(":var[");
  const newLines = valueLines(field.newValue, nameOf);
  const newFull = newLines.join("\n");
  const oldLines = hasOld ? valueLines(oldRaw, nameOf) : null;
  const oldFull = oldLines?.join("\n") ?? "";
  const isLong = [newFull, oldFull].some(
    (text) => text.length > SUMMARY_LENGTH || text.includes("\n")
  );
  const code =
    open && language && !field.table && typeof field.newValue === "string";
  // Text reads in the body font; code and structured values in monospace.
  const mono = !!language || typeof field.newValue !== "string";
  // Expanded, the old value takes its own lines and the arrow leads the new
  // one; code keeps the old summary on one line, the new code below it.
  const stacked = open && !code;
  const arrow = <span className="text-muted-foreground shrink-0">→</span>;
  const newValue = field.table ? (
    <EntityPill entity={{ kind: "table", id: field.table }} />
  ) : (
    !code && (
      <span
        className={cn(
          "min-w-0",
          mono && "font-mono text-[11px]",
          open ? "whitespace-pre-wrap break-words" : "truncate",
          isVar ? "text-primary/80" : "text-emerald-600"
        )}
      >
        {open ? newFull : truncate(newLines.join("; "))}
      </span>
    )
  );
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <span className={LABEL}>{humanize(field.name)}</span>
        {stacked ? (
          <div className="min-w-0 flex-1 space-y-0.5">
            {hasOld && (
              <div
                className={cn(
                  "text-red-400/80 line-through whitespace-pre-wrap break-words",
                  mono && "font-mono text-[11px]"
                )}
              >
                {oldFull}
              </div>
            )}
            <div className="flex items-baseline gap-1 min-w-0">
              {hasOld && arrow}
              {newValue}
            </div>
          </div>
        ) : (
          <div className="flex items-baseline gap-1 min-w-0 flex-1">
            {hasOld && (
              <>
                <span
                  className={cn(
                    "min-w-0 truncate text-red-400/80 line-through",
                    mono && "font-mono text-[11px]"
                  )}
                >
                  {truncate(oldLines!.join("; "))}
                </span>
                {arrow}
              </>
            )}
            {newValue}
          </div>
        )}
        {isLong && (
          <button
            type="button"
            aria-label={open ? "Collapse" : "Expand"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="shrink-0 self-start mt-0.5 cursor-pointer text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronDown
              className={cn(
                "size-3 transition-transform",
                open && "rotate-180"
              )}
            />
          </button>
        )}
      </div>
      {code && (
        <CodeBlock
          code={newFull}
          language={language}
          className="mt-1 ml-[3.5rem] w-auto max-h-40 overflow-auto [&_code]:text-[11px]! [&_pre]:p-2! [&_pre]:text-[11px]! [&_pre]:whitespace-pre-wrap"
        />
      )}
    </div>
  );
}

function EditRow({
  action,
  node,
  nameOf,
}: {
  action: Extract<WorkflowApprovalAction, { op: "edit" }>;
  node: ApprovalNode | undefined;
  nameOf: NodeNameOf;
}) {
  const display = useNodeTypeDisplay(node?.nodeType ?? "");
  const schemas = useNodeFieldSchemas(node?.nodeType ?? "");
  const label = handleLabel(action.handle, nameOf);
  const config = node?.nodeConfig ?? {};
  const values = {
    ...config,
    ...Object.fromEntries(action.fields.map((f) => [f.name, f.newValue])),
  };
  // A field set to the value it already has changes nothing.
  const changed = action.fields.filter(
    (field) =>
      !node ||
      JSON.stringify(config[field.name]) !== JSON.stringify(field.newValue)
  );
  return (
    <div className="flex items-start gap-2 text-xs pt-[6px]">
      <OpBadge op="edit" className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <NodeTypeIcon display={display} />
          {action.newName ? (
            <Renamed from={label} to={action.newName} />
          ) : (
            <span className="font-medium text-foreground">{label}</span>
          )}
        </div>
        <div className="mt-1.5 space-y-1 empty:hidden">
          {changed.length === 0 && !action.newName && (
            <p className="text-muted-foreground">
              Already set to these values, so nothing changes here
            </p>
          )}
          {changed.map((field) => {
            const oldRaw = node ? config[field.name] : undefined;
            if (Array.isArray(oldRaw) && Array.isArray(field.newValue)) {
              const changes = rowChanges(oldRaw, field.newValue, nameOf);
              return (
                <div key={field.name}>
                  <div className="flex items-baseline gap-2">
                    <span className={LABEL}>{humanize(field.name)}</span>
                    <span className="text-emerald-600">
                      {rowChangesSummary(changes)}
                    </span>
                  </div>
                  <RowChangeLines changes={changes} />
                </div>
              );
            }
            return (
              <ScalarFieldRow
                key={field.name}
                field={field}
                oldRaw={oldRaw}
                nameOf={nameOf}
                language={codeLanguageOf(schemas[field.name], values)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function EdgeRow({
  action,
  nameOf,
}: {
  action: Extract<WorkflowApprovalAction, { op: "edge" }>;
  nameOf: NodeNameOf;
}) {
  const removed = action.change === "disconnect";
  return (
    <div className="flex items-center gap-2 text-xs pt-[6px]">
      <OpBadge op={removed ? "remove" : "link"} />
      <div
        className={cn(
          "flex min-w-0 items-center gap-1 font-medium text-foreground",
          removed && "line-through decoration-red-400/70"
        )}
      >
        <span className="min-w-0 truncate">
          {handleLabel(action.source, nameOf)}
        </span>
        {action.branch && (
          <span className="shrink-0 font-normal text-muted-foreground">
            ({humanize(action.branch)})
          </span>
        )}
        <span className="shrink-0 text-muted-foreground">→</span>
        <span className="min-w-0 truncate">
          {handleLabel(action.target, nameOf)}
        </span>
      </div>
    </div>
  );
}

// Trigger types are raw engine identifiers ("schedule_v2") — show a human
// name; unknown types get the version suffix stripped and humanized.
// Fallback only — the human label comes from the trigger's own definition
// (backend AVAILABLE_TRIGGERS), used when its type isn't in the group's list.
function humanizeTriggerType(type: string): string {
  const base = type.replace(/_v\d+$/, "").replace(/_/g, " ");
  return base.charAt(0).toUpperCase() + base.slice(1);
}

// The trigger's display title is owned by its definition on the backend; only
// fall back to humanizing the raw engine type ("schedule_v2") when it's absent.
export function resolveTriggerTitle(
  type: string,
  triggers: readonly { type: string; title?: string | null }[] | undefined
): string {
  return (
    triggers?.find((t) => t.type === type)?.title ?? humanizeTriggerType(type)
  );
}

function ActionRows({ plan }: { plan: WorkflowApprovalPlan }) {
  const nodes = useApprovalFlowNodes(plan.workflowId);
  const nameOf = nodeNameOf(nodes, plan.actions);
  const { spotGroup } = useSpotUser();
  const hasTrigger = plan.actions.some((a) => a.op === "trigger");
  const { data: triggerConfig } = useRemoteData(
    DATA_KEYS.AVAILABLE_TRIGGERS,
    { path: { group_id: spotGroup.id } },
    { enabled: hasTrigger && !!spotGroup.id }
  );
  const triggerTitle = (type: string): string =>
    resolveTriggerTitle(type, triggerConfig?.triggers);
  return (
    <div className="space-y-1.5">
      {plan.actions.map((action, i) => {
        if (action.op === "add")
          return <AddRow key={i} action={action} nameOf={nameOf} />;
        if (action.op === "remove")
          return (
            <RemoveRow
              key={i}
              action={action}
              node={findNodeByHandle(nodes, action.handle)}
            />
          );
        if (action.op === "rename")
          return (
            <RenameRow
              key={i}
              action={action}
              node={findNodeByHandle(nodes, action.handle)}
            />
          );
        if (action.op === "edit")
          return (
            <EditRow
              key={i}
              action={action}
              node={findNodeByHandle(nodes, action.handle)}
              nameOf={nameOf}
            />
          );
        if (action.op === "edge")
          return <EdgeRow key={i} action={action} nameOf={nameOf} />;
        return (
          <div key={i} className="flex items-center gap-2 text-xs">
            <OpBadge op="trigger" />
            <span className="font-medium text-foreground">
              {triggerTitle(action.triggerType)}
            </span>
            <span className="text-foreground">trigger</span>
          </div>
        );
      })}
    </div>
  );
}

export function WorkflowApprovalBody({ plan }: { plan: WorkflowApprovalPlan }) {
  if (plan.actions.length === 0) return null;
  return <ActionRows plan={plan} />;
}
